import { useState, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from 'react-hook-form';
import { createFractionalLeaveAssignmentFormSchema, type CreateFractionalLeaveAssignmentFormValues } from '@/schemas/absence';
import { absenceService } from '@/api/domains/absence/absence.service';
import { ABSENCE_SCOPE_LIST_REFRESH_EVENT } from '@/api/domains/absence/absence.types';
import type { LC_FractionalLeaveTypeItemOut } from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { useAbortController } from '@/hooks/ui/useAbortController';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { injectValidationErrors } from '@/utils';
import { convertTimeToDecimal, appendSeconds, TIME_INPUT_REGEX, TIME_HH_MM_REGEX, isValidPartialDecimalHours } from '@/utils/timeConversion';


const CONFLICT_EXISTING_LEAVE_FOR_DAY_CODE = 'CONFLICT_EXISTING_LEAVE_FOR_DAY';
const NO_LATE_HOURS_DECIMAL_DISPLAY = '0,0000';
const NO_LATE_HOURS_TIME_DISPLAY = '00:00:00';

/**
 * Normaliza un valor de horas ingresado libremente (p. ej. "2", "2,0000", "14:30") al
 * formato HH:mm. Valores no interpretables se retornan sin cambios para que la
 * validación de formato los rechace.
 */
export function normalizeHoursToJustify(rawValue: string): string {
  const trimmed = rawValue.trim();
  if (!trimmed) return '';

  const timeMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})$/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    if (hours > 23 || minutes > 59) return trimmed;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  const decimalValue = Number(trimmed.replace(',', '.'));
  if (Number.isNaN(decimalValue) || decimalValue < 0 || decimalValue >= 24) return trimmed;

  const totalMinutes = Math.round(decimalValue * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function formatDecimalHours(decimalHours: number): string {
  return decimalHours.toFixed(4).replace('.', ',');
}

export interface UseCreateFractionalLeaveParams {
  /** Colaborador seleccionado en el selector compartido del panel (fuente única de verdad). */
  employeeTargetId: number | null;
}

export interface UseCreateFractionalLeaveResult {
  form: {
    register: UseFormRegister<CreateFractionalLeaveAssignmentFormValues>;
    setValue: UseFormSetValue<CreateFractionalLeaveAssignmentFormValues>;
    watch: UseFormWatch<CreateFractionalLeaveAssignmentFormValues>;
    errors: FieldErrors<CreateFractionalLeaveAssignmentFormValues>;
    trigger: UseFormTrigger<CreateFractionalLeaveAssignmentFormValues>;
  };
  isSubmitted: boolean;
  isSubmitting: boolean;
  handleSubmit: () => void;

  fractionalLeaveTypeOptions: SelectorOption[];
  isLoadingFractionalLeaveTypes: boolean;

  calculatedLateHoursDecimalDisplay: string;
  calculatedLateHoursTimeDisplay: string;
  isLoadingCalculatedLateHours: boolean;

  hoursToJustifyDecimalValue: string;
  handleHoursToJustifyDecimalChange: (value: string) => void;
  handleHoursToJustifyDecimalBlur: () => void;
  handleHoursToJustifyTimeChange: (value: string) => void;
  handleHoursToJustifyTimeBlur: () => void;
}

export function useCreateFractionalLeave(
  params: UseCreateFractionalLeaveParams,
): UseCreateFractionalLeaveResult {
  const { employeeTargetId } = params;
  const { t: translate } = useTranslation('absence');
  const { showApiError, showSuccess, showNotification, closeSidePanel } = useUI();

  // ── Fractional leave types ──────────────────────────────────────────────────
  // Sin AbortController: catálogo de una pasada, mismo criterio que useCreateSpecialLeave.ts
  // (evita TaskCanceledException por doble montaje en StrictMode).
  const [fractionalLeaveTypes, setFractionalLeaveTypes] =
    useState<LC_FractionalLeaveTypeItemOut[]>([]);
  const [isLoadingFractionalLeaveTypes, setIsLoadingFractionalLeaveTypes] = useState(false);

  const loadFractionalLeaveTypes = useCallback(async () => {
    setIsLoadingFractionalLeaveTypes(true);
    try {
      const response = await absenceService.getFractionalLeaveTypesForAssignment();
      if (response.success && response.data) {
        setFractionalLeaveTypes(response.data ?? []);
      } else {
        showApiError(response, translate('fractionalLeave.errors.loadFractionalLeaveTypesFailed'));
      }
    } finally {
      setIsLoadingFractionalLeaveTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadFractionalLeaveTypes();
  }, [loadFractionalLeaveTypes]);

  const fractionalLeaveTypeOptions = useMemo<SelectorOption[]>(
    () => fractionalLeaveTypes.map((type) => ({ value: String(type.id), label: type.name })),
    [fractionalLeaveTypes],
  );

  // ── Form ───────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    setValue,
    watch,
    getValues,
    trigger,
    setError,
    handleSubmit: rhfHandleSubmit,
    formState,
  } = useForm<CreateFractionalLeaveAssignmentFormValues>({
    resolver: zodResolver(createFractionalLeaveAssignmentFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      employeeTargetId: null,
      fractionalLeaveTypeId: null,
      startDate: '',
      hoursToJustify: '',
      detail: '',
    },
  });

  // El colaborador se completa en el selector compartido del panel (mismo buscador que
  // "Imputar asistencia"); este formulario solo lo refleja para poder validarlo junto al resto.
  useEffect(() => {
    setValue('employeeTargetId', employeeTargetId, { shouldDirty: true });
  }, [employeeTargetId, setValue]);

  // ── Atrasos calculados ───────────────────────────────────────────────────────
  const [calculatedLateHours, setCalculatedLateHours] =
    useState<{ decimalHours: number; formattedHours: string } | null>(null);
  const [isLoadingCalculatedLateHours, setIsLoadingCalculatedLateHours] = useState(false);
  const startDateForLateHours = watch('startDate');
  const debouncedStartDate = useDebounce(startDateForLateHours, 400);
  const { createSignal } = useAbortController();

  useEffect(() => {
    if (!employeeTargetId || !debouncedStartDate) {
      // Cancela el fetch en curso — evita que una respuesta tardía de una combinación
      // employeeTargetId/fecha anterior sobrescriba el null y deje el loading colgado.
      createSignal();
      setCalculatedLateHours(null);
      setIsLoadingCalculatedLateHours(false);
      return;
    }

    const signal = createSignal();
    setIsLoadingCalculatedLateHours(true);

    void (async () => {
      const response = await absenceService.getCalculatedLateHours(
        { employeeTargetId, date: debouncedStartDate },
        signal,
      );

      // petición cancelada (deps cambiaron o unmount), ignorar
      if (response.error?.code === 'ABORTED') return;

      if (response.success && response.data) {
        setCalculatedLateHours(response.data);
      } else {
        showApiError(response, translate('fractionalLeave.errors.loadCalculatedLateHoursFailed'));
        setCalculatedLateHours(null);
      }
      setIsLoadingCalculatedLateHours(false);
    })();
  }, [employeeTargetId, debouncedStartDate, showApiError, translate, createSignal]);

  const calculatedLateHoursDecimalDisplay = useMemo(() => {
    if (!calculatedLateHours) return NO_LATE_HOURS_DECIMAL_DISPLAY;
    return formatDecimalHours(calculatedLateHours.decimalHours);
  }, [calculatedLateHours]);

  const calculatedLateHoursTimeDisplay = useMemo(() => {
    if (!calculatedLateHours) return NO_LATE_HOURS_TIME_DISPLAY;
    return calculatedLateHours.formattedHours;
  }, [calculatedLateHours]);

  // ── Horas a justificar ──────────────────────────────────────────────────────
  // Campo dual: hoursToJustify (HH:mm) es el valor canónico enviado al backend; el
  // decimal es un espejo local sincronizado en el blur de cualquiera de los dos campos.
  const [hoursToJustifyDecimalValue, setHoursToJustifyDecimalValue] = useState('');

  // Solo refleja el decimal si el valor normalizado es una hora HH:mm válida; si no,
  // deja el espejo vacío para no mostrar "0,0000" como si fuera una conversión real.
  const syncDecimalFromTime = useCallback((timeValue: string) => {
    if (!TIME_HH_MM_REGEX.test(timeValue)) {
      setHoursToJustifyDecimalValue('');
      return;
    }
    const decimal = convertTimeToDecimal(appendSeconds(timeValue));
    setHoursToJustifyDecimalValue(decimal !== null ? formatDecimalHours(decimal) : '');
  }, []);

  // LabelInput no reenvía registration.onBlur al input nativo, por eso se usa getValues()
  // en vez del evento de blur; la validación se dispara en cada blur, no solo tras submit.
  const handleHoursToJustifyTimeBlur = useCallback(() => {
    const normalized = normalizeHoursToJustify(getValues('hoursToJustify') ?? '');
    setValue('hoursToJustify', normalized, { shouldDirty: true });
    syncDecimalFromTime(normalized);
    void trigger('hoursToJustify');
  }, [getValues, setValue, trigger, syncDecimalFromTime]);

  const handleHoursToJustifyTimeChange = useCallback((value: string) => {
    if (!TIME_INPUT_REGEX.test(value)) return;
    setValue('hoursToJustify', value, { shouldDirty: true });
  }, [setValue]);

  const handleHoursToJustifyDecimalChange = useCallback((value: string) => {
    if (!isValidPartialDecimalHours(value)) return;
    setHoursToJustifyDecimalValue(value);
  }, []);

  // Si el decimal no es una hora válida, no se propaga al campo canónico hoursToJustify
  // (evita mostrar un valor sin sentido ahí); el espejo decimal tampoco se toca.
  const handleHoursToJustifyDecimalBlur = useCallback(() => {
    const raw = hoursToJustifyDecimalValue.trim();
    const normalized = normalizeHoursToJustify(raw);

    if (TIME_HH_MM_REGEX.test(normalized)) {
      setValue('hoursToJustify', normalized, { shouldDirty: true });
      syncDecimalFromTime(normalized);
      void trigger('hoursToJustify');
      return;
    }

    if (!raw) {
      setValue('hoursToJustify', '', { shouldDirty: true });
      void trigger('hoursToJustify');
      return;
    }

    setError('hoursToJustify', { message: 'absence:fractionalLeave.validation.hoursToJustifyInvalidFormat' });
  }, [hoursToJustifyDecimalValue, setValue, trigger, setError, syncDecimalFromTime]);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const onValid = useCallback(
    async (data: CreateFractionalLeaveAssignmentFormValues) => {
      setIsSubmitting(true);
      try {
        const response = await absenceService.createFractionalLeaveAssignment({
          employeeTargetId: data.employeeTargetId as number,
          fractionalLeaveTypeId: data.fractionalLeaveTypeId as number,
          startDate: data.startDate,
          hoursToJustify: data.hoursToJustify,
          detail: data.detail || undefined,
        });

        if (response.success) {
          showSuccess(translate('fractionalLeave.messages.createSuccess'));
          window.dispatchEvent(new CustomEvent(ABSENCE_SCOPE_LIST_REFRESH_EVENT));
          closeSidePanel?.();
        } else if (response.error?.code === CONFLICT_EXISTING_LEAVE_FOR_DAY_CODE) {
          setError('startDate', { message: 'absence:fractionalLeave.errors.existingLeaveForDay' });
          showNotification('error', translate('fractionalLeave.errors.existingLeaveForDay'));
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('fractionalLeave.errors.createFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [closeSidePanel, setError, showApiError, showNotification, showSuccess, translate],
  );

  const handleSubmit = useCallback(() => {
    const wrappedValid = (data: CreateFractionalLeaveAssignmentFormValues) => {
      void onValid(data);
    };
    const wrappedInvalid = () => {};
    void rhfHandleSubmit(wrappedValid, wrappedInvalid)();
  }, [rhfHandleSubmit, onValid]);

  return {
    form: {
      register,
      setValue,
      watch,
      errors: formState.errors,
      trigger,
    },
    isSubmitted: formState.isSubmitted,
    isSubmitting,
    handleSubmit,
    fractionalLeaveTypeOptions,
    isLoadingFractionalLeaveTypes,
    calculatedLateHoursDecimalDisplay,
    calculatedLateHoursTimeDisplay,
    isLoadingCalculatedLateHours,
    hoursToJustifyDecimalValue,
    handleHoursToJustifyDecimalChange,
    handleHoursToJustifyDecimalBlur,
    handleHoursToJustifyTimeChange,
    handleHoursToJustifyTimeBlur,
  };
}

export default useCreateFractionalLeave;
